import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Avatar,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { getToken, base_url } from "../../config/config";
import axios from "axios";
import {
  Search,
  AddCircleOutlineOutlined,
  EditOutlined,
  DeleteOutlined,
  VisibilityOutlined,
  LocalShippingOutlined,
  MoreVert,
  ImageOutlined,
  PrintOutlined,
} from "@mui/icons-material";
import Barcode from "react-barcode";
import { useNavigate } from "react-router-dom";

const ProductsList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [selectedBarcodeProduct, setSelectedBarcodeProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${base_url}/v1/stock/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setProducts(
          response.data.data.map((product) => ({
            ...product,
            id: product._id,
            materialTypeName: product.materialType?.name || "Not specified",
            supplierCount: product.supplierInfo?.length || 0,
            totalValue: (product.stockQuantity * product.purchasePrice).toFixed(
              2
            ),
            // Add image URL for easy access
            imageUrl: product.image?.s3Url || null,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setAlert({
        open: true,
        message: "Failed to load products",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const token = getToken();
      const response = await axios.delete(
        `${base_url}/v1/stock/products/${productToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setProducts(
          products.filter((product) => product.id !== productToDelete.id)
        );
        setAlert({
          open: true,
          message: "Product deleted successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      setAlert({
        open: true,
        message: error.response?.data?.message || "Failed to delete product",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleOpenActionMenu = (event, product) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  const generateBarcodeValue = (product) => {
    const ownerName = product.stockOwner?.name || "Unknown";
    const diameter = product.diameter || "0";
    const location = product.location || "Unknown";

    return `${ownerName}-${diameter}-${location}`;
  };

  const handlePrintBarcode = () => {
    const printContent = document.getElementById("barcode-print");
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;

    // Reload the page to restore React functionality
    window.location.reload();
  };

  const handleOpenBarcodeDialog = (product) => {
    setSelectedBarcodeProduct(product);
    setBarcodeDialogOpen(true);
  };

  const handleCloseBarcodeDialog = () => {
    setBarcodeDialogOpen(false);
    setSelectedBarcodeProduct(null);
  };

  const getLocationDisplay = (location) => {
    switch (location) {
      case "stock":
        return "Hauptlager";
      case "production":
        return "Produktionsbereich";
      case "quality":
        return "Qualitätskontrolle";
      default:
        return location || "Unbekannt";
    }
  };

  const handleActionSelect = (action) => {
    handleCloseActionMenu();

    if (!selectedProduct) return;

    switch (action) {
      case "view":
        navigate(`/stock/products/${selectedProduct.id}`);
        break;
      case "edit":
        navigate(`/stock/products/edit/${selectedProduct.id}`);
        break;
      case "delete":
        setProductToDelete(selectedProduct);
        setDeleteDialogOpen(true);
        break;
      case "movement":
        navigate("/stock/movements/new", {
          state: { productId: selectedProduct.id },
        });
        break;
      case "barcode":
        handleOpenBarcodeDialog(selectedProduct);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      field: "image",
      headerName: "Image",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row.imageUrl ? (
            <Avatar
              src={params.row.imageUrl}
              alt={params.row.name}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1, // Makes it slightly rounded square
                objectFit: "cover",
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: colors.grey[700],
                color: colors.grey[300],
              }}
            >
              <ImageOutlined sx={{ fontSize: 20 }} />
            </Avatar>
          )}
        </Box>
      ),
    },
    { field: "artikelNummer", headerName: "SKU", flex: 1 },
    { field: "name", headerName: "Product Name", flex: 1.5 },
    { field: "materialTypeName", headerName: "Category", flex: 1 },
    { field: "unitName", headerName: "Unit", width: 80 },
    {
      field: "stockQuantity",
      headerName: "Stock Qty",
      type: "number",
      width: 110,
      renderCell: (params) => (
        <Typography
          color={
            params.row.stockQuantity <= params.row.minStockLevel
              ? colors.redAccent[500]
              : colors.grey[100]
          }
          fontWeight={
            params.row.stockQuantity <= params.row.minStockLevel
              ? "bold"
              : "normal"
          }
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "minStockLevel",
      headerName: "Min Level",
      type: "number",
      width: 110,
    },
    {
      field: "purchasePrice",
      headerName: "Price",
      width: 100,
      renderCell: (params) => (
        <Typography>€{params.value.toFixed(2)}</Typography>
      ),
    },
    {
      field: "totalValue",
      headerName: "Total Value",
      width: 120,
      renderCell: (params) => <Typography>€{params.value}</Typography>,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleOpenActionMenu(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchText.toLowerCase()) ||
      product.artikelNummer.toLowerCase().includes(searchText.toLowerCase()) ||
      product.materialTypeName.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            variant="h2"
            color={colors.grey[100]}
            fontWeight="bold"
            mb="5px"
          >
            Lagerprodukte
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Verwalten Sie Ihre Lagerartikel
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<VisibilityOutlined />}
            onClick={() => navigate("/stock/products/logs")}
          >
            Produkt-Logs
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleOutlineOutlined />}
            onClick={() => navigate("/stock/products/new")}
          >
            Neues Produkt hinzufügen
          </Button>
        </Box>
      </Box>

      {/* SEARCH BAR */}
      <TextField
        fullWidth
        variant="filled"
        label="Search Products"
        placeholder="Search by name, SKU, or category..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mt: 2, mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {/* DATA GRID */}
      <Box
        height="calc(100vh - 250px)"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          // Ensure proper row height for images
          "& .MuiDataGrid-row": {
            minHeight: "60px !important",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredProducts}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight={false}
            rowHeight={60} // Set consistent row height for images
          />
        )}
      </Box>

      {/* ACTION MENU */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={() => handleActionSelect("view")}>
          <VisibilityOutlined sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleActionSelect("edit")}>
          <EditOutlined sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleActionSelect("barcode")}>
          <PrintOutlined sx={{ mr: 1 }} /> Print Barcode
        </MenuItem>
        <MenuItem onClick={() => handleActionSelect("movement")}>
          <LocalShippingOutlined sx={{ mr: 1 }} /> Create Movement
        </MenuItem>
        <MenuItem
          onClick={() => handleActionSelect("delete")}
          sx={{ color: colors.redAccent[500] }}
        >
          <DeleteOutlined sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the product "{productToDelete?.name}"?
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteProduct} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* BARCODE DIALOG */}
      <Dialog
        open={barcodeDialogOpen}
        onClose={handleCloseBarcodeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" component="div">
            Produkt Barcode
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedBarcodeProduct && (
            <Box
              id="barcode-print"
              sx={{
                textAlign: "center",
                p: 3,
                bgcolor: "#fff",
                color: "#000",
                borderRadius: 1,
              }}
            >
              <Typography variant="h5" fontWeight="bold" mb={2}>
                {selectedBarcodeProduct.name}
              </Typography>
              <Box
                sx={{
                  border: "2px solid #000",
                  p: 2,
                  mb: 2,
                  display: "inline-block",
                  borderRadius: 1,
                }}
              >
                <Barcode
                  value={generateBarcodeValue(selectedBarcodeProduct)}
                  height={40}
                  width={1.5}
                  fontSize={14}
                />
              </Box>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                Barcode: {generateBarcodeValue(selectedBarcodeProduct)}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Besitzer:{" "}
                {selectedBarcodeProduct.stockOwner?.name || "Unbekannt"} •
                Durchmesser: {selectedBarcodeProduct.diameter || "0"} mm •
                Standort: {getLocationDisplay(selectedBarcodeProduct.location)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                SKU: {selectedBarcodeProduct.artikelNummer} • ID:
                {selectedBarcodeProduct._id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseBarcodeDialog} color="secondary">
            Schließen
          </Button>
          <Button
            onClick={handlePrintBarcode}
            startIcon={<PrintOutlined />}
            variant="contained"
            color="secondary"
          >
            Drucken
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsList;
